import { useState, type ReactNode } from 'react';
import { Alert, Button, Card, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  confirmContact,
  getContacts,
  sendContactCode,
  setContact,
  type PAContact,
  type PAContactCode,
  type PAContactKind,
  type PAContacts,
} from '../api/profile';
import { formatInstant } from '../format';
import { FactList } from '../money/DataTable';
import { QueryFrame } from '../money/MoneyPage';
import { usePAQuery } from '../money/usePAQuery';
import { contactChannelLabel } from '../money/labels';
import { errorText } from '../session/errorText';

/**
 * ============================================================================
 * «ПРОФИЛЬ» — the contacts and their CONFIRMATION (Appendix L §4, F4)
 * ============================================================================
 *
 * ⚠ THIS IS THE CUTOVER UNLOCK AND NOT A CONVENIENCE, which is why it is the first screen
 * of the wave and sits fourth on the strip. `subscriber.email_confirmed` and
 * `subscriber.mobile_confirmed` are `NOT NULL DEFAULT false`, and the notification engine
 * refuses an unconfirmed contact PER CHANNEL — so an imported subscriber who presses
 * «Забыли пароль?» reads «пароль отправлен» and receives nothing, and receives no
 * suspension notice, no payment confirmation and no dunning warning either. Until he has
 * been here he is a telephone call to the operator, and at cutover that is every imported
 * subscriber.
 *
 * # The four rules this screen is built to
 *
 *  1. **THE SERVER'S ANSWER IS THE STATE.** Both write routes return the FRESH contact
 *     list and this screen renders it — never the value it sent, and never an optimistic
 *     «Подтверждён». Editing a contact RESETS its confirmation (F4) while re-saving the
 *     SAME value keeps it, and no client-side rule can know which happened: the server
 *     compares what is stored. An optimistic screen would leave a green tick beside a
 *     contact that no longer has one, which is the one lie this screen must not tell —
 *     the tick IS the claim «messages reach me».
 *  2. **NO ADDRESS FIELD ON THE CODE STEP.** `POST …/{kind}/code` takes no body at all:
 *     the destination is read off the card by the server, so the route cannot be used to
 *     send the operator's SMS to an arbitrary number at his expense. A box there would
 *     look like it chose where the code goes while the server ignored it — the D109 class,
 *     with a security property behind it. The sentence «Указать другой адрес здесь нельзя»
 *     is what stands in its place.
 *  3. **NO «ПОДТВЕРДИТЬ» WHEN `notifier_available` IS FALSE.** Both code routes then answer
 *     503, so the button's only possible outcome is an error a subscriber cannot act on —
 *     on the very screen whose purpose is to make delivery work. An explanation is
 *     rendered instead, and an explanation is not a control.
 *  4. **`phone` IS DISPLAY-ONLY.** It has no confirmation column, no message is ever
 *     delivered to it and no route writes it, so it carries no «Изменить» and no
 *     «Подтвердить». The row says where it can be changed instead.
 *  5. **A CHANGED CONTACT COSTS THE PORTAL PASSWORD, AND AN UNCHANGED ONE MUST NOT.**
 *     *(Added 2026-08-12 with the server's `current_password` requirement — the Phase-7
 *     review's finding B1.)* A session alone was a full account takeover on this screen: the
 *     write resets confirmation, the code then necessarily goes to the NEW address, and the
 *     unauthenticated recovery route mails the plaintext password there and revokes the real
 *     subscriber's sessions. So the field appears exactly when the typed value DIFFERS from
 *     the one the server last returned, which is the server's own condition
 *     («ignored when it is the same»); prompting on a re-save would ask for a credential the
 *     server discards, on the commonest action here — correcting one's own typo. Nothing is
 *     re-derived that the server decides: the comparison is against the value the server
 *     itself just sent, and the refusal, its wording and its lockout stay entirely server-side.
 *
 * # Why a code is not sent automatically after a save
 *
 * §4's own prose says «On Save a confirmation code is sent», and this screen deliberately
 * does NOT chain the two calls. The code engine holds a DURABLE per-(subscriber, purpose)
 * cooldown and a windowed cap — at the defaults five codes an hour — so auto-sending on
 * every save would burn a slot for a subscriber who is merely fixing a typo, and the
 * fourth correction would answer `429 pa.contact_code_cooldown`: a refusal produced by the
 * screen's own helpfulness, which he cannot understand and cannot escape. §4's other
 * sentence — «the value shows a red "Confirm" action» — is the shape that ships.
 */
export function ProfileScreen() {
  const { t } = useTranslation();
  const { query, reload } = usePAQuery<PAContacts>(() => getContacts(), []);

  return (
    <>
      <h1 className="pa-page-title">{t('profile.title')}</h1>
      <p className="pa-page-lead">{t('profile.lead')}</p>
      <QueryFrame query={query} reload={reload}>
        {(contacts) => <ProfileBody fromServer={contacts} reload={reload} />}
      </QueryFrame>
    </>
  );
}

/** Which contact is being edited or confirmed. One at a time, so two forms cannot disagree. */
type Mode = { kind: PAContactKind; step: 'edit' | 'code' } | null;

function ProfileBody({ fromServer, reload }: { fromServer: PAContacts; reload: () => void }) {
  const { t } = useTranslation();
  /*
    ⚠ THE SERVER'S LATEST ANSWER SHADOWS THE QUERY'S, and it is `null` until there is one.

    Both write routes return the whole fresh list, so re-reading after a write would be a
    second request describing the same card at a different instant — the seam this project
    keeps paying for. `local ?? fromServer` is the whole mechanism: no effect copies the
    query into state (an effect that overwrote a just-written value would be invisible to
    every test that asserts on the FIRST render), and `refresh()` drops back to the query
    for the cases where the server has told us our copy is stale.
  */
  const [local, setLocal] = useState<PAContacts | null>(null);
  const contacts = local ?? fromServer;

  const [mode, setMode] = useState<Mode>(null);
  const [valueText, setValueText] = useState('');
  // Rule 5. Held here and CLEARED on every exit from the form — a portal password left in
  // React state after a save is a credential kept for no remaining purpose.
  const [passwordText, setPasswordText] = useState('');
  const [codeText, setCodeText] = useState('');
  const [code, setCode] = useState<PAContactCode | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState('');
  const [localError, setLocalError] = useState('');

  /** Drop our copy and re-read: the server has said the card is not what we think it is. */
  const refresh = () => {
    setLocal(null);
    reload();
  };

  const clearBanners = () => {
    setError(null);
    setNotice('');
    setLocalError('');
  };

  const startEdit = (kind: PAContactKind) => {
    clearBanners();
    setCode(null);
    setValueText(contacts[kind].value);
    setPasswordText('');
    setMode({ kind, step: 'edit' });
  };

  const save = async (kind: PAContactKind) => {
    const value = valueText.trim();
    if (value === '') {
      // The server refuses an empty value too (`pa.invalid_contact`) — an empty contact is
      // the silent removal of his only route back into the account, and is NOT a delete.
      // Refusing here as well spares him a round trip for a mistake he can see.
      setLocalError(t('profile.valueRequired'));
      return;
    }
    // Rule 5. `changesValue` is the SERVER's own condition for requiring the password, asked
    // against the value the server last returned — not a guess about what the write will do.
    if (changesValue(contacts, kind, value) && passwordText === '') {
      setLocalError(t('profile.passwordRequired'));
      return;
    }
    setBusy(true);
    clearBanners();
    try {
      const fresh = await setContact(kind, value, passwordText);
      setLocal(fresh);
      setPasswordText('');
      setMode(null);
      // WHICH sentence is decided by what came BACK, not by what was sent: a re-save of the
      // same value keeps the confirmation and only the server knows that.
      setNotice(fresh[kind].confirmed ? t('profile.savedConfirmed') : t('profile.saved'));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const requestCode = async (kind: PAContactKind) => {
    setBusy(true);
    clearBanners();
    try {
      const issued = await sendContactCode(kind);
      setCode(issued);
      setCodeText('');
      setMode({ kind, step: 'code' });
    } catch (err) {
      setError(err);
      // «Уже подтверждён» and «нет такого контакта» both mean our copy is stale: the state
      // that produced the button is not the state on the card.
      if (isStale(err)) refresh();
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (kind: PAContactKind) => {
    const entered = codeText.trim();
    if (entered === '') {
      setLocalError(t('profile.codeRequired'));
      return;
    }
    setBusy(true);
    setError(null);
    setLocalError('');
    try {
      const fresh = await confirmContact(kind, entered);
      setLocal(fresh);
      setMode(null);
      setCode(null);
      setNotice(t('profile.confirmedNow'));
    } catch (err) {
      setError(err);
      if (isStale(err)) {
        setMode(null);
        setCode(null);
        refresh();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {notice ? (
        <Alert type="success" showIcon message={notice} style={{ marginBottom: 16 }} />
      ) : null}
      {error ? (
        <Alert type="error" showIcon message={errorText(error, t)} style={{ marginBottom: 16 }} />
      ) : null}

      <Card className="pa-block">
        <FactList facts={identityFacts(contacts, t)} />

        {(['mobile', 'email'] as const).map((kind) => (
          <ContactBlock
            key={kind}
            kind={kind}
            contact={contacts[kind]}
            notifierAvailable={contacts.notifier_available}
            mode={mode}
            busy={busy}
            valueText={valueText}
            passwordText={passwordText}
            // Rule 5: the field is shown for exactly the condition the server applies.
            passwordNeeded={changesValue(contacts, kind, valueText.trim())}
            codeText={codeText}
            code={code}
            localError={localError}
            onValueText={(next) => {
              setLocalError('');
              setValueText(next);
            }}
            onPasswordText={(next) => {
              setLocalError('');
              setPasswordText(next);
            }}
            onCodeText={(next) => {
              setLocalError('');
              setCodeText(next);
            }}
            onEdit={() => startEdit(kind)}
            onCancel={() => {
              setMode(null);
              setCode(null);
              setLocalError('');
              setPasswordText('');
            }}
            onSave={() => void save(kind)}
            onRequestCode={() => void requestCode(kind)}
            onSubmitCode={() => void submitCode(kind)}
          />
        ))}
      </Card>
    </>
  );
}

/**
 * changesValue — does saving this actually MOVE the contact?
 *
 * ⚠ IT IS THE SERVER'S OWN CONDITION, ASKED OF THE SERVER'S OWN VALUE. `current_password` is
 * required when the value changes and ignored when it does not, so this decides whether the
 * field is shown and whether it is required — never what the write does, which stays entirely
 * the server's. The comparison is against `contacts[kind].value`, i.e. what the last response
 * carried, which is why an optimistic screen would break it: compare against what was TYPED
 * last time and a re-save would look like a change.
 *
 * `value` arrives trimmed; the stored side is trimmed too because the server stores it that
 * way — a trailing space in the box must not read as an edit and prompt for a password.
 */
function changesValue(contacts: PAContacts, kind: PAContactKind, value: string): boolean {
  return value !== contacts[kind].value.trim();
}

/**
 * isStale reports the two refusals that mean «the card is not what your screen thinks».
 *
 * `pa.contact_confirmed` — he confirmed it elsewhere (staff did, or another tab);
 * `pa.contact_missing` / `pa.contact_code_none` — the contact moved or was never entered.
 * In all three the list we are rendering produced a control the server has just refused, so
 * the honest response is to re-read rather than to leave the same button in place.
 */
function isStale(err: unknown): boolean {
  const code = (err as { code?: string } | null)?.code ?? '';
  return (
    code === 'pa.contact_confirmed' ||
    code === 'pa.contact_missing' ||
    code === 'pa.contact_code_none'
  );
}

/** The identity rows — read-only, `phone` among them. See rule 4 in the header. */
function identityFacts(
  contacts: PAContacts,
  t: TFunction,
): Array<{ key: string; label: string; value: ReactNode; hint?: ReactNode }> {
  const facts: Array<{ key: string; label: string; value: ReactNode; hint?: ReactNode }> = [
    {
      key: 'name',
      label: t('profile.nameLabel'),
      value: contacts.display_name,
    },
    {
      key: 'login',
      label: t('profile.loginLabel'),
      value: <span className="mono">{contacts.login}</span>,
    },
  ];
  // Rendered only when the operator has one: an empty row would invite an editor that
  // cannot exist.
  if (contacts.phone) {
    facts.push({
      key: 'phone',
      label: t('profile.phoneLabel'),
      value: <span className="mono">{contacts.phone}</span>,
      hint: t('profile.phoneHint'),
    });
  }
  return facts;
}

/**
 * One editable contact: the value, its confirmation badge, and whichever of the two forms
 * is open.
 *
 * ⚠ THE CODE PANEL CONTAINS ONE FIELD AND IT IS THE CODE. See rule 2 in the header.
 */
function ContactBlock({
  kind,
  contact,
  notifierAvailable,
  mode,
  busy,
  valueText,
  passwordText,
  passwordNeeded,
  codeText,
  code,
  localError,
  onValueText,
  onPasswordText,
  onCodeText,
  onEdit,
  onCancel,
  onSave,
  onRequestCode,
  onSubmitCode,
}: {
  kind: PAContactKind;
  contact: PAContact;
  notifierAvailable: boolean;
  mode: Mode;
  busy: boolean;
  valueText: string;
  passwordText: string;
  passwordNeeded: boolean;
  codeText: string;
  code: PAContactCode | null;
  localError: string;
  onValueText: (next: string) => void;
  onPasswordText: (next: string) => void;
  onCodeText: (next: string) => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onRequestCode: () => void;
  onSubmitCode: () => void;
}) {
  const { t } = useTranslation();
  const editing = mode?.kind === kind && mode.step === 'edit';
  const confirming = mode?.kind === kind && mode.step === 'code';
  const label = t(kind === 'mobile' ? 'profile.mobileLabel' : 'profile.emailLabel');
  const hint = t(kind === 'mobile' ? 'profile.mobileHint' : 'profile.emailHint');
  const placeholder = t(
    kind === 'mobile' ? 'profile.mobilePlaceholder' : 'profile.emailPlaceholder',
  );

  return (
    <div className="pa-subblock" data-testid={`pa-contact-${kind}`}>
      <span className="pa-field-label">{label}</span>
      <p className="pa-contact-value">
        <span className="mono">{contact.value || t('profile.notFilled')}</span>
        {contact.value ? (
          <span className={`pa-status pa-status-${contact.confirmed ? 'ok' : 'warn'}`}>
            {t(contact.confirmed ? 'profile.confirmed' : 'profile.unconfirmed')}
          </span>
        ) : null}
      </p>
      <p className="pa-field-hint">{hint}</p>
      {contact.value && !contact.confirmed ? (
        <p className="pa-field-hint">{t('profile.unconfirmedHint')}</p>
      ) : null}

      {editing ? (
        <div className="pa-inline-form">
          <label className="pa-field-label" htmlFor={`pa-contact-input-${kind}`}>
            {label}
          </label>
          <Input
            id={`pa-contact-input-${kind}`}
            size="large"
            autoComplete="off"
            inputMode={kind === 'mobile' ? 'tel' : 'email'}
            value={valueText}
            placeholder={placeholder}
            status={localError ? 'error' : undefined}
            onChange={(e) => onValueText(e.target.value)}
          />
          {/* ⚠ RULE 5. It appears the moment the typed value stops matching the stored one and
              disappears again if he types it back — the server's own condition, so the field
              is never asked for on a re-save it would discard. `Input.Password` and not a bare
              `type="password"`: the box holds his portal credential. */}
          {passwordNeeded ? (
            <>
              <label className="pa-field-label" htmlFor={`pa-contact-password-${kind}`}>
                {t('profile.passwordLabel')}
              </label>
              <Input.Password
                id={`pa-contact-password-${kind}`}
                size="large"
                autoComplete="current-password"
                value={passwordText}
                status={localError ? 'error' : undefined}
                onChange={(e) => onPasswordText(e.target.value)}
              />
              <p className="pa-field-hint">{t('profile.passwordHint')}</p>
            </>
          ) : null}
          {localError ? <p className="pa-field-error">{localError}</p> : null}
          <div className="pa-actions">
            <Button type="primary" size="large" block loading={busy} onClick={onSave}>
              {t('profile.save')}
            </Button>
            <Button size="large" block onClick={onCancel}>
              {t('profile.cancel')}
            </Button>
          </div>
        </div>
      ) : null}

      {confirming && code ? (
        <div className="pa-inline-form">
          <Alert
            type="info"
            showIcon
            message={t('profile.codeSentTitle')}
            description={
              <>
                <p className="pa-notice-note">
                  {t('profile.codeSent', {
                    channel: contactChannelLabel(code.channel, t),
                    when: formatInstant(code.expires_at),
                  })}
                </p>
                {/* THE SENTENCE THAT REPLACES AN ADDRESS FIELD — see rule 2. */}
                <p className="pa-notice-note">{t('profile.codeNoAddress')}</p>
              </>
            }
          />
          <label className="pa-field-label" htmlFor={`pa-contact-code-${kind}`}>
            {t('profile.codeLabel')}
          </label>
          <Input
            id={`pa-contact-code-${kind}`}
            size="large"
            autoComplete="one-time-code"
            inputMode="numeric"
            value={codeText}
            placeholder={t('profile.codePlaceholder')}
            status={localError ? 'error' : undefined}
            onChange={(e) => onCodeText(e.target.value)}
          />
          {localError ? <p className="pa-field-error">{localError}</p> : null}
          <div className="pa-actions">
            <Button type="primary" size="large" block loading={busy} onClick={onSubmitCode}>
              {t('profile.codeSubmit')}
            </Button>
            <Button size="large" block onClick={onRequestCode} loading={busy}>
              {t('profile.codeResend')}
            </Button>
            <Button size="large" block onClick={onCancel}>
              {t('profile.cancel')}
            </Button>
          </div>
        </div>
      ) : null}

      {!editing && !confirming ? (
        <div className="pa-actions">
          <Button size="large" block onClick={onEdit}>
            {t(contact.value ? 'profile.edit' : 'profile.add')}
          </Button>
          {/* ⚠ RULE 3: no «Подтвердить» when the installation cannot send anything. The
              sentence below takes its place — an explanation is not a control. */}
          {contact.value && !contact.confirmed && notifierAvailable ? (
            <Button type="primary" size="large" block loading={busy} onClick={onRequestCode}>
              {t('profile.confirm')}
            </Button>
          ) : null}
          {contact.value && !contact.confirmed && !notifierAvailable ? (
            <Alert type="warning" showIcon message={t('profile.notifierUnavailable')} />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
